import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CalendarDays, Plus, Trash2, CheckCircle, Save } from "lucide-react";
import { handleGet, handleRemove, handleSend, handleSetActiveYear } from "../../../controllers/controller.all";

export default function EcoleAnneeList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state for creating a new year
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        annee: "",
        date_deb: "",
        date_fin: ""
    });

    const fetchItems = async () => {
        setLoading(true);
        await handleGet(`/ecole-annee/ecole/${ecoleId}`, (res) => {
            if (res?.annees) {
                setItems(res.annees);
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if (token && ecoleId) fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);

    const handleDelete = async (id) => {
        await handleRemove(`/ecole-annee/${id}`, fetchItems, token);
    };

    const handleSetActive = async (id) => {
        await handleSetActiveYear(id, ecoleId, token, () => {
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        });
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = { ...formData, ecole: ecoleId };
        let res;
        if (editingId) {
            res = await handleSend(e, `/ecole-annee/${editingId}`, payload, null, token, "PUT");
        } else {
            res = await handleSend(e, "/ecole-annee/", payload, null, token);
        }
        if (res && res.state === "success") {
            setShowForm(false);
            setEditingId(null);
            setFormData({ annee: "", date_deb: "", date_fin: "" });
            fetchItems();
        }
    };

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Années Scolaires</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez les années académiques de votre établissement.</p>
                </div>
                <button
                    onClick={() => {
                        setShowForm(!showForm);
                        if (showForm) setEditingId(null);
                        setFormData({ annee: "", date_deb: "", date_fin: "" });
                    }}
                    className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                >
                    {showForm ? "Fermer" : <><Plus className="h-4 w-4" /> Nouvelle Année</>}
                </button>
            </div>

            {showForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Ajouter une année scolaire</h2>
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Année (ex: 2024-2025)</label>
                            <input required type="text" name="annee" value={formData.annee} onChange={handleChange} placeholder="2024-2025" className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Date de début</label>
                            <input required type="date" name="date_deb" value={formData.date_deb} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Date de fin</label>
                            <input required type="date" name="date_fin" value={formData.date_fin} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-indigo-500 outline-none" />
                        </div>
                        <div className="flex items-end">
                            <button type="submit" className="w-full inline-flex justify-center items-center gap-2 bg-indigo-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition">
                                <Save className="h-4 w-4" /> {editingId ? "Mettre à jour" : "Enregistrer"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Scolaire</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucune année scolaire trouvée.</td>
                                </tr>
                            ) : (
                                items.map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                                    <CalendarDays className="h-5 w-5" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-bold text-gray-900">{item.annee}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            Du {item.date_deb} au {item.date_fin}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {item.is_active ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    Inactif
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {!item.is_active && (
                                                <button
                                                    onClick={() => handleSetActive(item._id)}
                                                    className="text-green-600 hover:text-green-800 bg-green-50 px-3 py-2 rounded-lg transition inline-flex items-center gap-2 mr-2"
                                                    title="Définir comme active"
                                                >
                                                    <CheckCircle className="h-4 w-4" /> Activer
                                                </button>
                                            )}
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditingId(item._id);
                                                        setFormData({
                                                            annee: item.annee,
                                                            date_deb: item.date_deb,
                                                            date_fin: item.date_fin
                                                        });
                                                        setShowForm(true);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 p-2 rounded-lg transition"
                                                    title="Modifier"
                                                >
                                                    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item._id)}
                                                    className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
